import firebase from 'firebase/compat';
import { isEqual } from 'lodash';
import { useState, useEffect, useRef, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import useContactWrites from 'hooks/contacts/useContactWrites';
import useAuth from 'hooks/useAuth';
import useCalendar from 'hooks/useCalendar';
import useFirestore from 'hooks/useFirestore';
import useWebotServer from 'hooks/useWebotServer';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack5';
import {
  Grid,
  Stack,
  Button,
  Dialog,
  Divider,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  FormControl,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { LoadingButton } from '@material-ui/lab';
import { ConfirmationDialog } from 'components/__haim/Common/ConfirmationDialog';
import { ListingImagesUpload } from '../core/ListingImagesUpload';
import { getImagesData } from 'utils/imageValidation';
import {
  setInitialFiles,
  setInitialImages,
  calculateFinalFee,
  setInitialListing,
  setInitialAddress,
  initialFormColumns,
  setInitialOwnersData,
  setInitialBuyerOriginData,
  setInitialListingContacts,
  setInitialAddressCoordinates
} from 'utils/listings/new';
import { OwnersPicker } from '../../table/filters/OwnersPicker';
import { ListingDialogInputCheckboxes } from '../form/ListingDialogInputCheckboxes';
import { ListingDialogInputVisibility } from '../form/ListingDialogInputVisibility';
import { ListingDialogInputTexts } from '../form/ListingDialogInputTexts';
import { ListingDialogInputFee } from '../form/ListingDialogInputFee';
import { CloseListingDeal } from '../core/CloseListingDeal';
import { EntryCongratsBar } from '../core/EntryCongratsBar';
import { CloseListingDealDialog } from './CloseListingDealDialog';
import { ListingDialogInputStatus } from '../form/ListingDialogInputStatus';
import { ConfirmExitEditingDialog } from './ConfirmExitEditingDialog';
import { ListingDialogInputFloors } from '../form/ListingDialogInputFloors';
import { ListingDialogInputSelects } from '../form/ListingDialogInputSelects';
import { isListingNotRecentlyUpdated } from 'utils/listings/helper';
import { createOrUpdateListing } from 'utils/listings/operation';
import { ListingDialogConditionalInputs } from '../core/ListingDialogConditionalInputs';
import { ListingFilesUpload } from '../core/ListingFilesUpload';
import { getFilesData } from 'utils/fileValidation';
import { AddressFieldsPicker } from '../AddressFieldsPicker';
import {
  showApartmentNumberInput,
  ListingDialogInputApartmentNumber
} from '../form/ListingDialogInputApartmentNumber';
import {
  ListingSellerSection,
  listingContactsFromSellerDraft,
  sellerDraftFromListingContacts
} from 'components/_dashboard/listings/seller/ListingSellerSection';
import { persistListingSeller } from 'components/_dashboard/listings/seller/persistListingSeller';
import { ListingSourcePicker } from 'components/_dashboard/listings/seller/ListingSourcePicker';
import { contactIdForPhone } from 'components/_dashboard/listings/seller/sellerPhone';
import { ListingDialogInputEviction } from '../form/ListingDialogInputEviction';
import { normalizeUserEmail } from 'utils/calendar/officeTasks';
import { buildListingDisplayName } from 'utils/listings/display';
import { israelDayOf } from 'utils/listings/lease';
import { convertListingToProcessedListingData } from 'utils/listings/parser';
import {
  INITIAL_ENTRY_CONGRATS,
  buildEntryCongratsTask,
  entryCongratsCandidates,
  recordedEntryDay,
  resolveEntryCongrats,
  sellerAttendee
} from 'utils/listings/entryCongrats';
import type { EntryCongratsDraft, EntryCongratsPlan } from 'utils/listings/entryCongrats';
import { isRentedListing } from '../../../../../@types/sharedSchema';
import type { LeaseDatesPatch } from '../core/LeaseTermSection';
import type { ChangeEvent, SyntheticEvent } from 'react';
import type {
  AddressCoordinates,
  EditStatus,
  EditableListing,
  ListingWithAdditionalData
} from '../../../../../@types/listings/new';
import {
  commercialListingType,
  residentialListingType
} from '../../../../../@types/listings/unions';
import type {
  Status,
  CommercialListingType,
  ResidentialListingType
} from '../../../../../@types/listings/unions';
import type { ImagePreview, FileOrImagePreview } from '../../../../../@types/listings/imageData';
import type { AdditionalUserWithFee } from '../../../../../@types/listings/user';
import type { PlaceAutoCompleteIds } from '../../table/filters/PlaceAutocomplete';
import type { CreateAddressDetails } from 'components/_dashboard/listingsMatch/CreateAddressDialog';
import type {
  TransactionType,
  ProcessedListingAddress,
  ProcessedListingData
} from '../../../../../@types/sharedSchema';
import type { FileOrFilePreview, FilePreview } from '../../../../../@types/listings/fileData';

type NewListingDialogProps = {
  open: boolean;
  listing?: ListingWithAdditionalData;
  editStatus?: null | EditStatus;
  duplicating?: boolean;
  isAuthorized?: boolean;
  closeDealDialogOpen?: boolean;
  // Prefill the transaction-type select on CREATE (ignored when the form already has one,
  // i.e. editing/duplicating). Used by callers that know the context — e.g. creating a
  // rental listing from a landlord's contact card.
  defaultTransactionType?: TransactionType;
  onClose: () => void;
  onConfirm: () => void;
  onListingCreation?: (
    listingId: string,
    isExclusivitySet: boolean,
    createdListing?: Partial<ProcessedListingData>
  ) => void;
};

export function NewListingDialog({
  open,
  listing,
  editStatus,
  duplicating,
  isAuthorized,
  closeDealDialogOpen,
  defaultTransactionType,
  onClose,
  onConfirm,
  onListingCreation
}: NewListingDialogProps): JSX.Element {
  // setInitialListing returns a lazy initializer — wrap it so the default transaction
  // type applies on CREATE without touching an existing listing's value.
  const initialListingWithDefaults = (): EditableListing => {
    const columns = setInitialListing(listing)();

    return defaultTransactionType && !columns.transactionType
      ? { ...columns, transactionType: defaultTransactionType }
      : columns;
  };

  const [address, setAddress] = useState<ProcessedListingAddress[]>(setInitialAddress(listing));

  const [addressCoordinates, setAddressCoordinates] = useState<AddressCoordinates>(
    setInitialAddressCoordinates(listing)
  );

  const [editableFormColumns, setEditableFormColumns] = useState<EditableListing>(
    initialListingWithDefaults
  );

  const [ownersData, setOwnersData] = useState<AdditionalUserWithFee[]>(
    setInitialOwnersData(listing)
  );

  /**
   * The seller, as ONE editable draft rather than an array of picked contacts.
   *
   * `listingContacts` is still what gets stored (unchanged shape, so every
   * existing reader keeps working); this is just the form's view of its first
   * entry. The form edits one seller — a property has one owner side, and the
   * old multi-picker was never used for more.
   */
  const [seller, setSeller] = useState(() =>
    sellerDraftFromListingContacts(setInitialListingContacts(listing)())
  );
  const listingContacts = listingContactsFromSellerDraft(seller) ?? [];

  /**
   * The marketplace ads this listing stands for.
   *
   * Local state ONLY so the chips update as she links and unlinks — the writes
   * go straight to the server, which owns `sourceListings` and `sourceListingIds`
   * and writes them together. This is deliberately not part of the form's save:
   * both fields are in `NotEditableListing`, so submitting could not persist
   * them anyway, and a form-owned copy would drift from what the server holds.
   */
  const [sourceListings, setSourceListings] = useState(() => listing?.sourceListings ?? []);

  /**
   * The server returns the full id set after each link/unlink, which is the
   * authority. `sourceListings` here carries the rich refs (origin, url) for
   * display, so a NEWLY linked id has no ref yet — it gets a minimal one from
   * the matching candidate the picker already rendered.
   */
  const handleSourceListingsChange = (nextIds: string[]): void => {
    setSourceListings((current) =>
      nextIds.map(
        (id) =>
          current.find((ref) => ref.listingId === id) ?? {
            listingId: id,
            origin: "yad2" as const,
            linkedAt: firebase.firestore.Timestamp.now(),
            linkedBy: userEmail
          }
      )
    );
  };

  const [buyerOriginData, setBuyerOriginData] = useState<AdditionalUserWithFee[]>(
    setInitialBuyerOriginData(listing)
  );

  const [selectedImages, setSelectedImages] = useState<FileOrImagePreview[]>(
    setInitialImages(listing)
  );

  const [selectedFiles, setSelectedFiles] = useState<FileOrFilePreview[]>(setInitialFiles(listing));

  const [imagesPreview, setImagesPreview] = useState<ImagePreview[]>(setInitialImages(listing));

  const [filesPreview, setFilesPreview] = useState<FilePreview[]>(setInitialFiles(listing));

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmExitDialogOpen, setConfirmExitDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * The "congratulate on the entry" reminder this save may add (EntryCongratsBar). Offered only
   * while the save turns a SALE listing sold: one that was already sold when the dialog opened
   * never offers it, so editing it later cannot add a second reminder.
   */
  const [entryCongrats, setEntryCongrats] = useState<EntryCongratsDraft>(INITIAL_ENTRY_CONGRATS);

  const { createOrUpdateEvent } = useCalendar();
  const { enqueueSnackbar } = useSnackbar();

  // The listings row keeps this dialog mounted between opens: each opening starts from the defaults.
  useEffect(() => {
    if (open || closeDealDialogOpen) setEntryCongrats(INITIAL_ENTRY_CONGRATS);
  }, [open, closeDealDialogOpen]);

  /**
   * A save is in flight. A ref, not the `loading` state, because this is a
   * GUARD and it has to be true before React has re-rendered anything.
   *
   * The cost of a second submit is a second `real-estate-listings` document —
   * there is no dedupe anywhere behind this form — and on the listings board it
   * would additionally link the same market ad to two different copies.
   */
  const savingRef = useRef(false);

  /**
   * The caller's `onListingCreation` arguments, held until the form is done.
   *
   * ⚠ THE CALLER IS CALLED LAST, from `onSuccess`, not the moment the document
   * exists. Almost every caller closes or unmounts this dialog from that callback
   * (the board settles its optimistic row, the signature form and the contact
   * dialog set their own `open` to false), so calling it earlier means the rest of
   * this form's own bookkeeping — the reset, clearing `loading` — either warns
   * about setting state on an unmounted component or is simply lost.
   *
   * The ordering callers depend on is preserved exactly: `onListingCreation`
   * still fires before `onConfirm`.
   */
  const pendingCreationRef = useRef<{
    listingId: string;
    isExclusivitySet: boolean;
    createdListing?: Partial<ProcessedListingData>;
  } | null>(null);

  const { organizationId, organizations, organizationUsers } = useFirestore();

  // The two writers the seller section needs after the listing exists: the server
  // contact writes (`useContactWrites` — edit an existing contact, or create one), and
  // `linkListingContact`, which stamps the resolved contact id back onto the listing.
  const contactWrites = useContactWrites();
  const { linkListingContact, contactsGet } = useWebotServer();

  const hasMultipleOrganizations = organizations.length > 1;

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(
    listing?.createdByOrganization ?? organizationId ?? ''
  );

  useEffect(() => {
    if (!listing && !selectedOrganizationId && organizationId) {
      setSelectedOrganizationId(organizationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const { t: translate } = useTranslation();

  const { showModal } = useModal();

  const {
    user: { email: userEmail }
  } = useAuth();

  const { userSettings, listingData } = useFirestore();

  const {
    fee,
    floor,
    status,
    videoUrl,
    floorOutOf,
    visibility,
    purposeType,
    listingType,
    description,
    listingState,
    additionalNotes,
    apartmentNumber,
    feeIncludesTaxes,
    transactionType,
    addressDisplayMode,
    userDefinedListingId
  } = editableFormColumns;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => cleanImages, []);

  useEffect(() => {
    if (!listing || loading) return;

    if (duplicating) return;

    if (!isAuthorized) {
      onClose();
      return;
    }

    const isNotRecentlyUpdated = isListingNotRecentlyUpdated(
      listing.updatedTime,
      editableFormColumns.updatedTime
    );

    const ignoreListingFormUpdate = isNotRecentlyUpdated && (open || closeDealDialogOpen);

    if (ignoreListingFormUpdate) return;

    setAddress(setInitialAddress(listing));
    setAddressCoordinates(setInitialAddressCoordinates(listing));
    setEditableFormColumns(initialListingWithDefaults);
    setOwnersData(setInitialOwnersData(listing));
    setBuyerOriginData(setInitialBuyerOriginData(listing));
    setSelectedImages(setInitialImages(listing));
    setImagesPreview(setInitialImages(listing));
    setSelectedFiles(setInitialFiles(listing));
    setFilesPreview(setInitialFiles(listing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closeDealDialogOpen, listing, duplicating, isAuthorized]);

  useEffect(() => {
    if (listing && !duplicating) return;

    if (!userSettings || !listingData) return;

    const { ownerFullName, ownerPhoneNumber, photoURL } = userSettings;
    const { listingAlias } = listingData;

    setOwnersData([
      {
        fee: null,
        name: ownerFullName,
        email: userEmail,
        finalFee: null,
        avatarUrl: photoURL,
        listingBio: userSettings.minisiteTagline ?? userSettings.minisiteCustomShortBio ?? '',
        phoneNumber: ownerPhoneNumber,
        listingAlias: listingAlias,
        feeIncludesTaxes: false
      }
    ]);
  }, [listing, userEmail, userSettings, listingData, duplicating]);

  useEffect(() => {
    if (listing || !organizationId) return;

    setEditableFormColumns({
      ...editableFormColumns,
      visibility: 'organization'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, listing, open]);

  useEffect(() => {
    if (!duplicating) return;

    setEditableFormColumns({
      ...editableFormColumns,
      visibility: 'private',
      // A copy of a rented property is a new listing: it does not inherit the original's
      // rental agreement, or its realtor would be reminded about both.
      leaseStartDate: null,
      leaseEndDate: null
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicating]);

  useEffect(() => {
    const setSoldFromMenuRow = editStatus === 'sold';

    if (!setSoldFromMenuRow) return;

    addDefaultFeeToOwnersData(editStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editStatus]);

  useEffect(() => {
    if (!purposeType || !listingType) return;

    let listingTypeIsValidForPurposeType = false;

    if (purposeType === 'residential') {
      listingTypeIsValidForPurposeType = residentialListingType.includes(
        listingType as ResidentialListingType
      );
    }

    if (purposeType === 'commercial') {
      listingTypeIsValidForPurposeType = commercialListingType.includes(
        listingType as CommercialListingType
      );
    }

    if (listingTypeIsValidForPurposeType) return;

    setEditableFormColumns({
      ...editableFormColumns,
      listingType: null
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purposeType]);

  const handleChange = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>): void => {
    setEditableFormColumns({
      ...editableFormColumns,
      [name]: value ?? null
    });
  };

  // The lease block sets two dates at once (a period button fills the start and the end),
  // which two `handleChange` calls in one tick cannot do: each spreads the same stale state.
  // The cast is the form's standing convention: its date fields hold `YYYY-MM-DD` strings
  // under Timestamp-typed keys (see `setInitialListing`).
  const handleLeaseChange = (lease: LeaseDatesPatch): void => {
    setEditableFormColumns((current) => ({
      ...current,
      ...(lease as unknown as Partial<EditableListing>)
    }));
  };

  const handleAutoCompleteChange =
    (type: PlaceAutoCompleteIds) =>
    (_event: SyntheticEvent, newInputValue: string): void => {
      setEditableFormColumns({
        ...editableFormColumns,
        [type]: newInputValue
      });
    };

  const handleAutoCompleteFill = (data: Partial<CreateAddressDetails>): void => {
    setEditableFormColumns({
      ...editableFormColumns,
      ...data
    });
  };

  const handleImageUpload = ({ target: { files } }: ChangeEvent<HTMLInputElement>): void => {
    const imagesData = getImagesData(files, imagesPreview.length);

    if (!imagesData) {
      setSnackbarOpen(true);
      return;
    }

    const { imagesPreviewData, selectedImagesData } = imagesData;

    setImagesPreview([...imagesPreview, ...imagesPreviewData]);
    setSelectedImages([...selectedImages, ...selectedImagesData]);
  };

  const handleFileUpload = ({ target: { files } }: ChangeEvent<HTMLInputElement>): void => {
    const filesData = getFilesData(files, filesPreview.length, 128);

    if (!filesData) {
      setSnackbarOpen(true);
      return;
    }

    const { filesPreviewData, selectedFilesData } = filesData;

    setFilesPreview([...filesPreview, ...filesPreviewData]);
    setSelectedFiles([...selectedFiles, ...selectedFilesData]);
  };

  const handleAddressChange = (newAddress: ProcessedListingAddress[]): void => {
    setAddress(newAddress);

    if (!newAddress.length) {
      setAddressCoordinates(null);
      return;
    }

    const newAddressCoordinates = newAddress[0]?.coords;

    if (newAddressCoordinates) {
      setAddressCoordinates({
        lat: newAddressCoordinates.lat,
        lon: newAddressCoordinates.lon
      });
    }
  };

  /**
   * Turn the typed seller into a real, linked contact. Runs once the listing
   * document exists, because every link needs its id.
   *
   * Shared by create and edit deliberately: a realtor routinely records the owner
   * on a listing she created weeks earlier, and the call is idempotent (an
   * already-linked contact just gets the same array written back).
   *
   * It never throws — `persistListingSeller` swallows its own failures, because
   * the listing is already written and losing a link is not worth losing the save.
   */
  const persistSellerForListing = async (listingId: string): Promise<void> => {
    const result = await persistListingSeller({
      listingId,
      // Always a `real-estate-listings` id here: this form is what creates and
      // edits that document. Saying so is what keeps the link step from being
      // attempted against a mirror id and half-failing in silence — the bug the
      // board's badge used to have.
      listingIdKind: 'realEstateListing',
      seller,
      // The contact she PICKED, or the one `EditContactDialog` just created.
      // Authoritative when present: `persistListingSeller` then reads that exact
      // document instead of re-deriving an id from the phone, so a legacy
      // duplicate stored under another spelling of the same number cannot win.
      knownContactId: seller.contactId,
      // Her explicit answer to "also add this person to my contacts", NOT the
      // default `true`. On this form recording the owner and creating a CRM card
      // are separate decisions — see `ListingSellerDraft.createContact`. A phone
      // that ALREADY belongs to a contact is still linked; this only governs
      // whether a new document may be minted.
      createContact: Boolean(seller.createContact),
      transactionType: editableFormColumns.transactionType,
      userEmail,
      contactsGet,
      writes: contactWrites,
      linkListingContact
      // `contactOrigin` is deliberately not passed: `persistListingSeller`
      // defaults it to one constant for every call site, so the same action is
      // countable however it was reached and whatever language the UI is in.
    });

    // Keep the draft in step with what was actually written, so a second save
    // from this same open dialog names the exact document rather than resolving
    // the phone all over again.
    if (result.contactId && result.contactId !== seller.contactId) {
      setSeller((current) => ({ ...current, contactId: result.contactId }));
    }
  };

  /**
   * Blank the form so the SAME dialog can be used to create another listing.
   *
   * ⚠ FROM-SCRATCH CREATE ONLY (`!listing`). On the listings page this dialog
   * stays mounted between listings, so without this the second "נכס חדש" would
   * open holding the first one's data. Everywhere else the dialog is unmounted
   * right after — and on the DUPLICATE path resetting was actively harmful: the
   * form blanked itself while still on screen, which is what the realtor reported
   * as "the modal emptied, offered to save again, then closed by itself".
   *
   * Everything the realtor entered is cleared, not just the columns. The address
   * and the seller are the two that matter: carrying either into the next listing
   * means a duplicate at the same address, or a second property silently attached
   * to the previous owner's contact.
   */
  const resetForNextCreate = (): void => {
    setEditableFormColumns(initialFormColumns);
    setAddress([]);
    setAddressCoordinates(null);
    setSeller(sellerDraftFromListingContacts(null));
    setBuyerOriginData([]);
    setImagesPreview([]);
    setSelectedImages([]);
    setFilesPreview([]);
    setSelectedFiles([]);
    setSourceListings([]);

    cleanImages();
  };

  /**
   * Hand control back to whoever opened us, in the order they expect:
   * `onListingCreation` (create only) and then `onConfirm`.
   *
   * Called LAST, after every state update this form owns, because these are the
   * callbacks that close and unmount the dialog.
   */
  const handOffToCaller = (): void => {
    const pending = pendingCreationRef.current;
    pendingCreationRef.current = null;

    // Wrapped because the listing is already written. A caller's post-save work
    // (confetti, injecting the new listing into a picker, the board's optimistic
    // row) throwing must not surface as "failed to create listing" — she would
    // save again and get a second document. Logged, never swallowed silently.
    try {
      if (pending) {
        onListingCreation?.(pending.listingId, pending.isExclusivitySet, pending.createdListing);
      }
    } catch (error) {
      console.error('A listing-creation callback threw after the listing was saved:', error);
    }

    try {
      onConfirm();
    } catch (error) {
      console.error('A listing dialog onConfirm handler threw after the save:', error);
    }
  };

  const createListing = async (): Promise<void> => {
    await createOrUpdateListing({
      address,
      userEmail,
      ownersData,
      selectedFiles,
      selectedImages,
      buyerOriginData,
      listingContacts,
      addressCoordinates,
      editableFormColumns,
      operationProps: {
        organizationId: selectedOrganizationId || organizationId,
        // Awaited by `createOrUpdateListing` — that is the fix for the reported
        // "it stopped loading, then closed on its own 3 seconds later": this
        // handler's promise used to be dropped, so `onSuccess` below ran while
        // the seller was still being written and re-armed the save button on an
        // already-blanked form.
        onListingCreation: async (listingId, isExclusivitySet, createdListing) => {
          await persistSellerForListing(listingId);
          pendingCreationRef.current = { listingId, isExclusivitySet, createdListing };
        }
      },
      onError: () => handleError('createListing'),
      onSuccess: () => {
        // Everything this form owns is settled first; the caller comes last.
        if (!listing) resetForNextCreate();
        setLoading(false);

        handOffToCaller();
      }
    });
  };

  const editListing = async (): Promise<void> => {
    // Read at submit: the bar as the realtor confirmed it, not as it reads once the form resets.
    const congratsPlan = isSellingNow && entryCongrats.enabled ? entryCongratsPlan : null;

    // Update the listing in Firebase - Madlan sync will be handled automatically by Firebase function
    await createOrUpdateListing({
      address,
      userEmail,
      ownersData,
      selectedFiles,
      selectedImages,
      buyerOriginData,
      listingContacts,
      addressCoordinates,
      editableFormColumns,
      operationProps: {
        listing,
        editStatus,
        updatedOrganizationId: hasMultipleOrganizations ? selectedOrganizationId : undefined
      },
      onError: () => handleError('updateListing'),
      onSuccess: async (pictures: ImagePreview[]) => {
        if (listing?.id) await persistSellerForListing(listing.id);

        setLoading(false);
        setImagesPreview(pictures ?? []);
        cleanImages();

        if (congratsPlan) void addEntryCongratsReminder(congratsPlan);

        handOffToCaller();
      }
    });
  };

  /**
   * Adds the reminder once the sale is saved, without holding the dialog open for it: a
   * colleague's task waits on the server, and on its WhatsApp for up to 10 seconds. It never
   * throws and never touches the sale; a failure only tells the realtor it was not added.
   */
  const addEntryCongratsReminder = async (plan: EntryCongratsPlan): Promise<void> => {
    const reportNotAdded = (): void => {
      enqueueSnackbar(translate('listingsPage.entryCongrats.notAdded'), { variant: 'error' });
    };

    if (!listing?.id || !plan.day || !createOrUpdateEvent) {
      reportNotAdded();
      return;
    }

    try {
      const taskListing = convertListingToProcessedListingData(listing);
      taskListing.id = listing.id;

      const title = [
        sellerName
          ? translate('listingsPage.entryCongrats.taskTitle', { name: sellerName })
          : translate('listingsPage.entryCongrats.taskTitleNoName'),
        buildListingDisplayName(listing, '')
      ]
        .filter(Boolean)
        .join(' · ');

      const task = buildEntryCongratsTask({
        title,
        day: plan.day,
        attendee: sellerAttendee({
          firstName: seller.firstName,
          lastName: seller.lastName,
          phoneInternational: seller.contactId || contactIdForPhone(seller.phone)
        }),
        listingData: taskListing
      });

      const isMine = plan.assigneeEmail === me;

      // A colleague's task goes through the office-task route, which reports its own outcome.
      const saved = await createOrUpdateEvent(
        task,
        isMine ? undefined : { assigneeUserId: plan.assigneeEmail }
      );

      if (!isMine) return;

      if (saved) {
        enqueueSnackbar(translate('listingsPage.entryCongrats.addedToMine'), { variant: 'success' });
      } else {
        reportNotAdded();
      }
    } catch (error) {
      console.error('The entry congratulations reminder failed after the sale was saved:', error);
      reportNotAdded();
    }
  };

  const handleError = (type: 'createListing' | 'updateListing'): void => {
    showModal(ConfirmationDialog, {
      title: translate(`listingsPage.error.${type}`),
      description: translate('listingsPage.error.tryAgain')
    });

    setLoading(false);
  };

  const isEditOperation = Boolean(listing) && !duplicating;

  const runSave = async (): Promise<void> => {
    try {
      if (isEditOperation) await editListing();
      else await createListing();
    } catch (error) {
      // `createOrUpdateListing` reports its own write failures through `onError`,
      // so reaching here means a success handler threw. Without this the form
      // would sit spinning with `savingRef` stuck true and no way out.
      console.error('Listing save failed after the write:', error);
      handleError(isEditOperation ? 'updateListing' : 'createListing');
    } finally {
      savingRef.current = false;
    }
  };

  const handleSubmit = (e?: FormEvent<HTMLFormElement>): void => {
    e?.preventDefault();

    // ONE save per submit. `loading` disables the button, but the form is also
    // submitted implicitly (Enter in a text field) and from
    // CloseListingDealDialog, and nothing downstream would dedupe a second
    // create — see `savingRef`.
    if (savingRef.current) return;
    savingRef.current = true;

    setLoading(true);

    void runSave();
  };

  const removeImage = (targetId: string) => (): void => {
    setImagesPreview(imagesPreview.filter(({ id }) => id !== targetId));
    setSelectedImages(selectedImages.filter(({ id }) => id !== targetId));

    const { src } = imagesPreview.find(({ id }) => id === targetId);

    URL.revokeObjectURL(src);
  };

  // Apply a new image order. We must reorder `selectedImages` to match, because
  // the saved `pictures` array (and thus cover = pictures[0]) is derived from
  // `selectedImages` order in `createOrUpdateListing` / `uploadImages`.
  const reorderImages = (reordered: ImagePreview[]): void => {
    setImagesPreview(reordered);

    setSelectedImages((previous) => {
      const byId = new Map(previous.map((image) => [image.id, image]));

      return reordered
        .map(({ id }) => byId.get(id))
        .filter((image): image is FileOrImagePreview => Boolean(image));
    });
  };

  const removeFile = (targetId: string) => (): void => {
    setFilesPreview(filesPreview.filter(({ id }) => id !== targetId));
    setSelectedFiles(selectedFiles.filter(({ id }) => id !== targetId));

    const { src } = filesPreview.find(({ id }) => id === targetId) || {};

    URL.revokeObjectURL(src);
  };

  const handleChangeChecked = ({
    target: { checked, name }
  }: ChangeEvent<HTMLInputElement>): void => {
    setEditableFormColumns({
      ...editableFormColumns,
      [name]: checked
    });
  };

  const handleSnackbarClose = (): void => setSnackbarOpen(false);

  const cleanImages = (): void => imagesPreview.forEach(({ src }) => URL.revokeObjectURL(src));

  const addDefaultFeeToOwnersData = (targetStatus: Status): void => {
    const { price, finalListingPrice } = editableFormColumns;

    const canBeFillWithDefaultFeeData =
      targetStatus === 'sold' && ownersData.length && fee && price;

    if (!canBeFillWithDefaultFeeData) return;

    const parsedPrice = finalListingPrice ?? price;

    const newOwnersDataWithSplittedFee = ownersData.map((ownerData) => {
      const splittedFee = fee / ownersData.length;
      const calculatedFinalFee = calculateFinalFee(fee, parsedPrice, feeIncludesTaxes);

      return {
        ...ownerData,
        fee: splittedFee,
        finalFee: calculatedFinalFee,
        feeIncludesTaxes: feeIncludesTaxes
      };
    });

    setOwnersData(newOwnersDataWithSplittedFee);
  };

  const handleConfirmCloseContinue = (): void => {
    // A save in flight owns the dialog. Every dismissal lands here — the X, the
    // backdrop and Escape — and letting one through mid-write would leave the
    // seller and link writes running with nothing on screen to say they had not
    // finished. `loading` always clears (onSuccess, onError, or `runSave`'s
    // catch), so this can never wedge the dialog shut.
    if (loading) return;

    const initialListing = setInitialListing(listing)();

    const currentPictures = imagesPreview.length
      ? imagesPreview.map(({ id, src, alt }) => ({ id, src, alt }))
      : null;

    const currentOwnersData = ownersData.map(({ email }) => email);

    const currentListing = {
      ...editableFormColumns,
      pictures: currentPictures,
      owners: currentOwnersData
    };

    /**
     * The seller is edited here too, and it is not part of `EditableListing`.
     *
     * Compared as DRAFTS, both sides through `sellerDraftFromListingContacts`, so
     * the two shapes are produced by the same normalization: comparing a draft
     * against a raw stored array would call every listing dirty whose seller row
     * carries a blank field or an emptied `contactId`, and a "discard changes?"
     * prompt on a listing nobody touched is how people learn to click through it.
     *
     * (`address` is deliberately not compared. It is a separate pre-existing gap,
     * and `AddressFieldsPicker` can enrich its entry on mount, so folding it in
     * here risks exactly the spurious prompt described above.)
     */
    const initialSeller = sellerDraftFromListingContacts(setInitialListingContacts(listing)());

    const isListingEdited =
      !isEqual(initialListing, currentListing) || !isEqual(initialSeller, seller);

    const showConfirmModal = listing && isListingEdited;

    if (showConfirmModal) handleConfirmExitDialogOpen();
    else onClose();
  };

  const handleCloseListingCloseExit = (): void => {
    handleConfirmExitDialogClose();
    onClose();
  };

  const handleConfirmExitDialogOpen = (): void => setConfirmExitDialogOpen(true);

  const handleConfirmExitDialogClose = (): void => setConfirmExitDialogOpen(false);

  // "Rented" is a rent listing whose deal closed. The row menu's "הושכר" arrives as
  // `editStatus` and is applied only on save, so the form's own `status` is still the old one.
  const isClosingAsRented = isRentedListing({
    status: editStatus === 'sold' ? 'sold' : status,
    transactionType
  });

  const isSellingNow =
    isEditOperation &&
    listing?.status !== 'sold' &&
    (editStatus || status) === 'sold' &&
    !isClosingAsRented;

  const me = normalizeUserEmail(userEmail);
  const todayDay = israelDayOf(new Date());
  const entryCongratsCandidateList = entryCongratsCandidates(ownersData, organizationUsers, me);
  const entryDay = recordedEntryDay(
    editableFormColumns.evictionType,
    editableFormColumns.evictionDate,
    todayDay
  );
  const entryCongratsPlan = resolveEntryCongrats(entryCongrats, {
    candidates: entryCongratsCandidateList,
    me,
    entryDay,
    todayDay
  });
  const sellerName = [seller.firstName, seller.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

  const entryCongratsBar = isSellingNow ? (
    <EntryCongratsBar
      loading={loading}
      sellerName={sellerName}
      draft={entryCongrats}
      plan={entryCongratsPlan}
      candidates={entryCongratsCandidateList}
      entryDay={entryDay}
      todayDay={todayDay}
      onChange={setEntryCongrats}
    />
  ) : null;

  const closeListingDeal = (
    <CloseListingDeal
      loading={loading}
      ownersData={ownersData}
      buyerOriginData={buyerOriginData}
      organizationUsers={organizationUsers}
      editableFormColumns={editableFormColumns}
      isClosingAsRented={isClosingAsRented}
      entryCongrats={entryCongratsBar}
      handleChange={handleChange}
      onLeaseChange={handleLeaseChange}
      onOwnersDataChange={setOwnersData}
      onBuyerOriginDataChange={setBuyerOriginData}
    />
  );

  return (
    <>
      {listing && (
        <CloseListingDealDialog
          open={closeDealDialogOpen}
          loading={loading}
          onClose={handleConfirmCloseContinue}
          handleSubmit={handleSubmit}
        >
          {closeListingDeal}
        </CloseListingDealDialog>
      )}
      <ConfirmExitEditingDialog
        open={confirmExitDialogOpen}
        listing={listing}
        onClose={handleCloseListingCloseExit}
        onCloseContinue={handleConfirmExitDialogClose}
      />
      <Dialog open={open} onClose={handleConfirmCloseContinue} maxWidth="md">
        <FormControl sx={{ overflow: 'hidden' }} component="form" onSubmit={handleSubmit}>
          <DialogTitle sx={{ py: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {translate(
                  `listingsPage.${listing ? (duplicating ? 'duplicate' : 'edit') : 'new'}Listing`
                )}
              </Typography>
              {/* Disabled while saving, to match the Cancel button below —
                  `handleConfirmCloseContinue` refuses anyway, and a control that
                  silently does nothing reads as a broken dialog. */}
              <IconButton disabled={loading} onClick={handleConfirmCloseContinue}>
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography>{translate('listingsPage.featuresOfHouse')}</Typography>
            <Grid sx={{ mt: 2 }} container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <ListingDialogInputSelects
                  name="purposeType"
                  value={purposeType}
                  loading={loading}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <ListingDialogInputSelects
                  required
                  name="listingType"
                  value={listingType}
                  loading={loading}
                  purposeType={purposeType}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <ListingDialogInputSelects
                  name="transactionType"
                  value={transactionType}
                  loading={loading}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <AddressFieldsPicker required disabled={loading} address={address} onAddressChange={handleAddressChange} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <ListingDialogInputSelects
                  name="addressDisplayMode"
                  value={addressDisplayMode}
                  loading={loading}
                  handleChange={handleChange}
                />
              </Grid>
              <ListingDialogInputTexts
                loading={loading}
                customComponentsInput={{
                  fee: {
                    customComponent: (
                      <ListingDialogInputFee
                        fee={fee}
                        loading={loading}
                        feeIncludesTaxes={feeIncludesTaxes}
                        handleChange={handleChange}
                        handleChangeChecked={handleChangeChecked}
                      />
                    )
                  },
                  apartmentNumber: {
                    customComponent: (
                      <ListingDialogInputApartmentNumber
                        loading={loading}
                        listingType={listingType}
                        apartmentNumber={apartmentNumber}
                        handleChange={handleChange}
                      />
                    ),
                    customCheck: () => showApartmentNumberInput(listingType)
                  }
                }}
                editableFormColumns={editableFormColumns}
                handleChange={handleChange}
                handleAutoCompleteFill={handleAutoCompleteFill}
                handleAutoCompleteChange={handleAutoCompleteChange}
              />
              <ListingDialogInputFloors
                floor={floor}
                loading={loading}
                floorOutOf={floorOutOf}
                listingType={listingType}
                handleChange={handleChange}
              />
              <ListingDialogConditionalInputs
                loading={loading}
                editableFormColumns={editableFormColumns}
                handleChange={handleChange}
              />
              <Grid item xs={12}>
                <ListingDialogInputEviction
                  loading={loading}
                  editableFormColumns={editableFormColumns}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <ListingDialogInputCheckboxes
                  loading={loading}
                  editableFormColumns={editableFormColumns}
                  handleChangeChecked={handleChangeChecked}
                />
              </Grid>

              {/*
                WHO, and WHERE ELSE IT IS PUBLISHED.

                One block, sitting after the property facts and before the
                marketing content, because the two questions belong together and
                are answered from the same phone call: who owns this, and which
                published ads am I standing in for. They used to be wedged between
                the property inputs, which read as two panels bolted into the
                middle of a form.

                Both sub-sections carry their own `overline` heading — the section
                grammar this dialog's siblings already use — so the dividers only
                have to delimit the group.
              */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
              </Grid>

              <Grid item xs={12}>
                {/*
                  Replaces the old contacts Autocomplete, and then puts it back
                  alongside: that picker could only choose an EXISTING contact,
                  which gets the workflow backwards for a realtor naming the owner
                  mid-call — while three free-text fields alone left someone
                  already in her book reachable only by typing their number from
                  memory. Pick or type; either way the contact is resolved or
                  created on save and linked both ways. See ListingSellerSection.

                  The two schemas the deleted picker had to reconcile still meet
                  here, and the distinction survives it: the LISTING stores the
                  seller's phone under `callerPhoneNumber`, while a CONTACT's phone
                  IS its document id. `contactIdForPhone` is now the single place
                  that conversion happens.
                */}
                <ListingSellerSection
                  loading={loading}
                  value={seller}
                  onChange={setSeller}
                  transactionType={editableFormColumns.transactionType}
                />
              </Grid>

              {/*
                EDIT ONLY. Linking needs a listing id, and on create there is
                nothing to link to yet — the create-from-a-market-ad path records
                its own link (useLinkListingToMarketAd). `duplicating` is also
                excluded: that flow creates a NEW document, so the id in hand
                belongs to the listing being copied FROM. The section is never
                rendered empty-headed: with no links it says so and offers the
                search.
              */}
              {listing?.id && !duplicating && (
                <Grid item xs={12}>
                  <ListingSourcePicker
                    disabled={loading}
                    listingId={listing.id}
                    value={sourceListings}
                    onChange={handleSourceListingsChange}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ mt: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={translate('listingsPage.tableColumns.description')}
                  value={description}
                  name="description"
                  disabled={loading}
                  onChange={handleChange}
                  inputProps={{ style: { resize: 'vertical' } }}
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <ListingImagesUpload
                  loading={loading}
                  snackbarOpen={snackbarOpen}
                  imagesPreview={imagesPreview}
                  removeImage={removeImage}
                  handleImageUpload={handleImageUpload}
                  handleSnackbarClose={handleSnackbarClose}
                  onReorder={reorderImages}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="videoUrl"
                  label={translate('listingsPage.videoUrl')}
                  placeholder={translate('listingsPage.videoUrlPlaceholder')}
                  value={videoUrl ?? ''}
                  disabled={loading}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={translate('listingsPage.tableColumns.additionalNotes')}
                  value={additionalNotes}
                  name="additionalNotes"
                  disabled={loading}
                  onChange={handleChange}
                  inputProps={{ style: { resize: 'vertical' } }}
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <ListingFilesUpload
                  loading={loading}
                  snackbarOpen={snackbarOpen}
                  filesPreview={filesPreview}
                  removeFile={removeFile}
                  handleFileUpload={handleFileUpload}
                  handleSnackbarClose={handleSnackbarClose}
                />
              </Grid>
              {!!organizationUsers.length && (
                <Grid item xs={12} sm={8}>
                  <OwnersPicker
                    type="new"
                    required
                    loading={loading}
                    ownersData={ownersData}
                    organizationUsers={organizationUsers}
                    onOwnersChange={setOwnersData}
                  />
                </Grid>
              )}
              {hasMultipleOrganizations && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={translate('listingsPage.organization')}
                    value={selectedOrganizationId}
                    disabled={loading}
                    onChange={(e) => setSelectedOrganizationId(e.target.value)}
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={4} justifyContent="center">
                <ListingDialogInputVisibility
                  loading={loading}
                  visibility={visibility}
                  organizationId={organizationId}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <ListingDialogInputStatus
                  status={status}
                  loading={loading}
                  transactionType={transactionType}
                  isAuthorized={isAuthorized}
                  handleChange={handleChange}
                  addDefaultFeeToOwnersData={addDefaultFeeToOwnersData}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <ListingDialogInputSelects
                  name="listingState"
                  value={listingState}
                  loading={loading}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  name="userDefinedListingId"
                  label={translate('listingsPage.tableColumns.userDefinedListingId')}
                  value={userDefinedListingId}
                  disabled={loading}
                  onChange={handleChange}
                />
              </Grid>
              {['sold', 'inNegotiation'].includes(status) && (
                <Grid item xs={12}>
                  {closeListingDeal}
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="error"
              disabled={loading}
              onClick={handleConfirmCloseContinue}
            >
              {translate('general.cancel')}
            </Button>
            <LoadingButton type="submit" color="info" variant="contained" loading={loading}>
              {translate('general.confirm')}
            </LoadingButton>
          </DialogActions>
        </FormControl>
      </Dialog>
    </>
  );
}
